import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { authApi } from '../services/api';
import {
  getToken,
  persistTokens,
  clearAllTokens,
  getRefreshToken,
} from '../services/tokenStorage';
import { silentRefresh } from '../services/authRefresh';
import {
  subscribeAuthSync,
  publishSession,
  publishLogout,
  SESSION_KEY,
} from '../services/authSync';
import { getOrCreateClientDeviceId } from '../services/deviceId';

const AuthContext = createContext(null);

function normalizeRole(raw = '') {
  return raw.toLowerCase();
}

function normalizeUser(userData, token) {
  const firstName = (userData.firstName ?? '').toString().trim();
  const lastName = (userData.lastName ?? '').toString().trim();
  const fromParts = `${firstName} ${lastName}`.trim();
  const fullName = fromParts || (userData.name || '').trim() || (userData.fullName || '').trim();
  const parts = fullName.split(/\s+/).filter(Boolean);
  const rawRole =
    userData.role ||
    (userData.isAdmin ? 'ADMIN' : userData.isDriver ? 'DRIVER' : userData.isOperator ? 'OPERATOR' : 'USER');
  return {
    ...userData,
    fullName,
    firstName: firstName || parts[0] || '',
    lastName: lastName || parts.slice(1).join(' ') || '',
    role: normalizeRole(rawRole),
    mahallaId: userData.mahallaId ?? userData.mahalla?.id ?? null,
    mahalla: userData.mahalla ?? null,
    token,
  };
}

function readCachedSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSession(user) {
  if (user) localStorage.setItem(SESSION_KEY, JSON.stringify(user));
  else localStorage.removeItem(SESSION_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBooting, setIsBooting] = useState(true);
  const [pendingVerification, setPendingVerification] = useState(null);
  const bootingRef = useRef(false);

  const applySession = useCallback((userData, accessToken, refreshToken, { broadcast = false } = {}) => {
    if (accessToken || refreshToken) {
      persistTokens(accessToken, refreshToken);
    }
    const token = accessToken || getToken();
    const normalized = normalizeUser(userData, token);
    setUser(normalized);
    writeSession(normalized);
    if (broadcast) {
      publishSession({
        accessToken: token,
        refreshToken: refreshToken || getRefreshToken(),
        user: normalized,
        clientDeviceId: getOrCreateClientDeviceId(),
      });
    }
    return normalized;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // ignore
    }
    clearAllTokens();
    writeSession(null);
    setUser(null);
    setPendingVerification(null);
    publishLogout();
  }, []);

  // Cross-tab: login / refresh / logout in another tab
  useEffect(() => {
    return subscribeAuthSync({
      onSession: async (event) => {
        if (bootingRef.current) return;
        // Only sync tokens from the same browser profile (same origin + device id).
        if (event.clientDeviceId && event.clientDeviceId !== getOrCreateClientDeviceId()) return;

        if (event.accessToken) persistTokens(event.accessToken, event.refreshToken);

        if (event.user) {
          const token = event.accessToken || getToken();
          const normalized = normalizeUser(event.user, token);
          setUser(normalized);
          writeSession(normalized);
          return;
        }

        try {
          const meResp = await authApi.getMe();
          if (meResp?.user) {
            applySession(meResp.user, getToken(), getRefreshToken());
          }
        } catch {
          // ignore
        }
      },
      onLogout: () => {
        clearAllTokens();
        writeSession(null);
        setUser(null);
        setPendingVerification(null);
      },
    });
  }, [applySession]);

  useEffect(() => {
    const onBalance = async () => {
      try {
        const response = await authApi.getMe();
        if (response?.user && user) {
          applySession({ ...user, ...response.user }, getToken(), getRefreshToken());
        }
      } catch {
        // ignore
      }
    };
    const onUnauthorized = () => {
      logout();
    };
    const onProfileUpdated = (e) => {
      const detail = e.detail;
      if (detail?.id) {
        applySession(detail, getToken(), getRefreshToken());
      }
    };
    window.addEventListener('smartgaz:balance-updated', onBalance);
    window.addEventListener('smartgaz:unauthorized', onUnauthorized);
    window.addEventListener('smartgaz:profile-updated', onProfileUpdated);
    return () => {
      window.removeEventListener('smartgaz:balance-updated', onBalance);
      window.removeEventListener('smartgaz:unauthorized', onUnauthorized);
      window.removeEventListener('smartgaz:profile-updated', onProfileUpdated);
    };
  }, [user, logout, applySession]);

  useEffect(() => {
    async function boot() {
      bootingRef.current = true;

      const cached = readCachedSession();
      if (cached) {
        setUser(cached);
        if (cached.token) persistTokens(cached.token, getRefreshToken());
      }

      const hasSessionHint = cached || getRefreshToken() || getToken();

      try {
        const refreshResult = await silentRefresh();
        const accessToken = refreshResult.accessToken || getToken();

        if (!accessToken) {
          throw new Error('REFRESH_FAILED');
        }

        const meResp = await authApi.getMe();
        if (meResp?.user) {
          applySession(meResp.user, accessToken, refreshResult.refreshToken || getRefreshToken());
        } else {
          throw new Error('GET_ME_EMPTY');
        }
      } catch (err) {
        const isNetwork =
          err.message === 'Failed to fetch' ||
          err.message?.includes('network') ||
          err.name === 'TypeError';

        if (isNetwork && cached) {
          if (cached.token) persistTokens(cached.token, getRefreshToken());
          setUser(cached);
        } else if (!hasSessionHint) {
          clearAllTokens();
          writeSession(null);
          setUser(null);
        } else if (cached) {
          // Refresh failed but had prior session — keep UI, next API call will retry refresh
          if (cached.token) persistTokens(cached.token, getRefreshToken());
          setUser(cached);
        } else {
          clearAllTokens();
          writeSession(null);
          setUser(null);
        }
      } finally {
        bootingRef.current = false;
        setIsBooting(false);
      }
    }

    boot();
  }, [applySession]);

  const login = useCallback(
    async ({ identifier, password }) => {
      setIsLoading(true);
      try {
        const isEmail = identifier.includes('@');
        const payload = {
          password,
          ...(isEmail ? { email: identifier } : { phone: identifier }),
        };

        const data = await authApi.login(payload);

        if (data.status === 'FAILED') {
          if (data.reason === 'USER_NOT_VERIFIED') {
            setPendingVerification({
              ...data.verification,
              identifier,
            });
          }
          throw new Error(data.reason || 'LOGIN_FAILED');
        }

        const { accessToken, refreshToken, user: userData } = data;
        if (!userData) throw new Error('USER_DATA_NOT_FOUND');

        return applySession(userData, accessToken, refreshToken, { broadcast: true });
      } finally {
        setIsLoading(false);
      }
    },
    [applySession],
  );

  const register = useCallback(
    async ({ fullName, firstName, lastName, phone, email, password, mahallaId }) => {
      setIsLoading(true);
      try {
        const name = fullName || `${firstName || ''} ${lastName || ''}`.trim();
        const data = await authApi.register({
          name,
          firstName,
          lastName,
          phone,
          email,
          password,
          mahallaId,
        });

        if (data.status === 'FAILED') {
          throw new Error(data.reason || data.message || 'REGISTRATION_FAILED');
        }

        if (data.status === 'SUCCESS') {
          const { accessToken, refreshToken, user: userData } = data;
          return applySession(userData, accessToken, refreshToken, { broadcast: true });
        }

        if (data.status === 'PENDING_VERIFICATION') {
          setPendingVerification({
            ...data.verification,
            identifier: phone || email,
          });
          return data;
        }

        return data;
      } finally {
        setIsLoading(false);
      }
    },
    [applySession],
  );

  const verifyOtp = useCallback(
    async (requestId, code) => {
      setIsLoading(true);
      try {
        const data = await authApi.verifyOtp(requestId, code);
        if (data.status !== 'SUCCESS') throw new Error(data.reason || 'OTP_VERIFICATION_FAILED');

        setPendingVerification(null);
        const { accessToken, refreshToken, user: userData } = data;
        return applySession(userData, accessToken, refreshToken, { broadcast: true });
      } finally {
        setIsLoading(false);
      }
    },
    [applySession],
  );

  const verifyEmail = useCallback(
    async (email, code) => {
      setIsLoading(true);
      try {
        const data = await authApi.verifyEmail({ email, code });
        if (data.status !== 'SUCCESS') throw new Error(data.reason || 'EMAIL_VERIFICATION_FAILED');

        setPendingVerification(null);
        const { accessToken, refreshToken, user: userData } = data;
        return applySession(userData, accessToken, refreshToken, { broadcast: true });
      } finally {
        setIsLoading(false);
      }
    },
    [applySession],
  );

  const resendCode = useCallback(async () => {
    if (!pendingVerification) return;
    setIsLoading(true);
    try {
      if (pendingVerification.email) {
        return await authApi.resendEmailCode(pendingVerification.email);
      }
      if (pendingVerification.phone) {
        const data = await authApi.resendOtp(pendingVerification.phone);
        if (data.requestId) {
          setPendingVerification((prev) => ({ ...prev, requestId: data.requestId }));
        }
        return data;
      }
    } finally {
      setIsLoading(false);
    }
  }, [pendingVerification]);

  const updateProfile = useCallback(
    async (updates) => {
      setIsLoading(true);
      try {
        const { userApi } = await import('../services/api');
        const updated = await userApi.updateProfile(updates);
        const { fullName: _oldFn, name: _oldName, ...userRest } = user || {};
        const session = applySession({ ...userRest, ...updated }, getToken(), getRefreshToken(), {
          broadcast: true,
        });
        window.dispatchEvent(new CustomEvent('smartgaz:profile-updated', { detail: session }));
        return session;
      } finally {
        setIsLoading(false);
      }
    },
    [user, applySession],
  );

  const refreshUser = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await authApi.getMe();
      if (response?.user) {
        return applySession(response.user, getToken(), getRefreshToken(), { broadcast: true });
      }
    } catch (err) {
      console.error('refreshUser error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [applySession]);

  const fetchAllUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const { userApi } = await import('../services/api');
      const allUsers = await userApi.getAllUsers();
      setUsers(Array.isArray(allUsers) ? allUsers : []);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteUser = useCallback(
    async (id) => {
      setIsLoading(true);
      try {
        const { userApi } = await import('../services/api');
        await userApi.deleteUser(id);
        await fetchAllUsers();
      } finally {
        setIsLoading(false);
      }
    },
    [fetchAllUsers],
  );

  const createUser = useCallback(
    async (userData) => {
      setIsLoading(true);
      try {
        await authApi.register(userData);
        await fetchAllUsers();
      } finally {
        setIsLoading(false);
      }
    },
    [fetchAllUsers],
  );

  const updateUser = useCallback(
    async (id, updates) => {
      if (id === user?.id) return updateProfile(updates);
      console.warn('Admin updateUser not implemented');
    },
    [user, updateProfile],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        users,
        isLoading,
        isBooting,
        pendingVerification,
        login,
        register,
        logout,
        verifyOtp,
        verifyEmail,
        resendCode,
        updateProfile,
        refreshUser,
        updateUser,
        deleteUser,
        fetchAllUsers,
        createUser,
        setPendingVerification,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
